import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { forwardRef } from "react";

const DynamicVariantSection = forwardRef(({ 
  variants, 
  setSelectedVariant, 
  selectedOptions, 
  setSelectedOptions, 
  setOutOfStock 
}, ref) => {
  // Extract all option types and their unique values
  const optionTypes = useMemo(() => {
    if (!variants?.length) return [];
    
    const typesMap = {};
    
    variants.forEach((variant) => {
      const optionValues = variant?.option_values_with_hex || {};
      
      Object.keys(optionValues).forEach((optionTypeName) => {
        if (!typesMap[optionTypeName]) {
          typesMap[optionTypeName] = {
            name: optionTypeName,
            hasHexCode: false,
            values: new Set(),
            valuesWithHex: new Map(),
          };
        }
        
        const optionValue = optionValues[optionTypeName];
        // Handle both object format {value: "...", hex_code: "..."} and direct value
        const value = typeof optionValue === 'object' && optionValue !== null 
          ? (optionValue.value || optionValue) 
          : optionValue;
        const hexCode = typeof optionValue === 'object' && optionValue !== null 
          ? optionValue.hex_code 
          : null;
        
        if (value) {
          typesMap[optionTypeName].values.add(value);
          
          // Check if hex_code exists and is a string (including "000000" for black)
          if (hexCode !== null && hexCode !== undefined && typeof hexCode === 'string') {
            typesMap[optionTypeName].hasHexCode = true;
            typesMap[optionTypeName].valuesWithHex.set(value, hexCode);
          }
        }
      });
    });
    
    // Convert to array format
    return Object.values(typesMap)?.map((type) => ({
      ...type,
      values: Array.from(type.values),
      valuesWithHex: Object.fromEntries(type.valuesWithHex),
    }));
  }, [variants]);

  // Initialize selected options with first value of each type
  useEffect(() => {
    if (optionTypes?.length > 0 && Object?.keys(selectedOptions || {})?.length === 0) {
      const initialOptions = {};
      optionTypes?.forEach((optionType) => {
        if (optionType?.values?.length > 0) {
          initialOptions[optionType?.name] = optionType?.values?.[0];
        }
      });
      setSelectedOptions(initialOptions);
    }
    // eslint-disable-next-line
  }, [optionTypes]);

  // Find matching variant based on selected options
  useEffect(() => {
    if (Object?.keys(selectedOptions || {})?.length === optionTypes?.length && optionTypes?.length > 0) {
      const matchingVariant = variants?.find((variant) => {
        const variantOptions = variant?.option_values_with_hex || {};
        return optionTypes?.every((optionType) => {
          const selectedValue = selectedOptions?.[optionType?.name];
          const variantOptionValue = variantOptions?.[optionType?.name];
          // Handle both object format and direct value
          const variantValue = typeof variantOptionValue === 'object' && variantOptionValue !== null
            ? (variantOptionValue?.value || variantOptionValue)
            : variantOptionValue;
          return selectedValue === variantValue;
        });
      });

      if (matchingVariant) {
        setSelectedVariant?.(matchingVariant);
      } else {
        setSelectedVariant?.(null);
      }
    } else {
      setSelectedVariant?.(null);
    }
    // eslint-disable-next-line
  }, [selectedOptions, variants, optionTypes]);

  // Handle option change
  const handleOptionChange = (optionTypeName, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionTypeName]: value,
    }));
  };

  // Get available values for an option type based on other selected options
  const getAvailableValues = (optionTypeName) => {
    const otherSelectedOptions = { ...(selectedOptions || {}) };
    delete otherSelectedOptions[optionTypeName];

    const matchingVariants = variants?.filter((variant) => {
      const variantOptions = variant?.option_values_with_hex || {};
      return Object?.keys(otherSelectedOptions || {})?.every?.((key) => {
        const selectedValue = otherSelectedOptions?.[key];
        const variantOptionValue = variantOptions?.[key];
        // Handle both object format and direct value
        const variantValue = typeof variantOptionValue === 'object' && variantOptionValue !== null
          ? (variantOptionValue?.value || variantOptionValue)
          : variantOptionValue;
        return selectedValue === variantValue;
      });
    }) || [];

    return matchingVariants
      ?.filter((variant) => (variant?.quantity || 0) > 0)
      ?.map((variant) => {
        const optionValue = variant?.option_values_with_hex?.[optionTypeName];
        // Handle both object format and direct value
        return typeof optionValue === 'object' && optionValue !== null
          ? (optionValue?.value || optionValue)
          : optionValue;
      }) || [];
  };

  // Check if current selection is out of stock
  useEffect(() => {
    if (Object?.keys(selectedOptions || {})?.length === optionTypes?.length && optionTypes?.length > 0) {
      const matchingVariant = variants?.find((variant) => {
        const variantOptions = variant?.option_values_with_hex || {};
        return optionTypes?.every((optionType) => {
          const selectedValue = selectedOptions?.[optionType?.name];
          const variantOptionValue = variantOptions?.[optionType?.name];
          // Handle both object format and direct value
          const variantValue = typeof variantOptionValue === 'object' && variantOptionValue !== null
            ? (variantOptionValue?.value || variantOptionValue)
            : variantOptionValue;
          return selectedValue === variantValue;
        });
      });

      if (matchingVariant) {
        setOutOfStock?.((matchingVariant?.quantity || 0) <= 0);
      } else {
        setOutOfStock?.(true);
      }
    }
    // eslint-disable-next-line
  }, [selectedOptions, variants, optionTypes]);

  if (!optionTypes?.length) {
    return null;
  }

  return (
    <>
      {optionTypes?.map((optionType, index) => {
        const availableValues = getAvailableValues(optionType?.name) || [];
        const selectedValue = selectedOptions?.[optionType?.name];
        const isLastOption = index === (optionTypes?.length || 0) - 1;

        if (optionType?.hasHexCode) {
          // Render as color selector
          return (
            <Box key={optionType?.name || index} className="type-container">
              <Typography className="type">
                {optionType?.name || ''} <span className="color-value">{selectedValue || ''}</span>
              </Typography>
              <Box className="check-container">
                {optionType?.values?.map((value, i) => {
                  const hexCode = optionType?.valuesWithHex?.[value];
                  const isAvailable = availableValues?.includes(value) || false;
                  return (
                    <Box
                      key={`${optionType?.name || index}-${value}-${i}`}
                      sx={{
                        border: selectedValue === value ? "1.5px solid black" : "1.5px solid transparent",
                        padding: "2px",
                        opacity: isAvailable ? 1 : 0.5,
                      }}
                    >
                      <Box
                        className="color-check"
                        sx={{
                          backgroundColor: hexCode ? `${hexCode}` : "#ccc",
                          cursor: isAvailable ? "pointer" : "not-allowed",
                          border: "0.1px solid grey",
                        }}
                        onClick={() => isAvailable && handleOptionChange(optionType?.name, value)}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        } else {
          // Render as text selector (like size)
          return (
            <Box 
              key={optionType?.name || index} 
              ref={isLastOption ? ref : null}
              className="type-container"
            >
              <Typography className="type">
                {optionType?.name || ''}: <span className="size-value">{selectedValue || ''}</span>
              </Typography>
              <Box className="size-options-container">
                {optionType?.values?.map((value, i) => {
                  const isAvailable = availableValues?.includes(value) || false;
                  return (
                    <Box
                      key={`${optionType.name}-${value}-${i}`}
                      className="size-options"
                      sx={{
                        backgroundColor: isAvailable
                          ? selectedValue === value
                            ? "#A78D78"
                            : "transparent"
                          : "#F0F0F0",
                        color: isAvailable
                          ? selectedValue === value
                            ? "white"
                            : "black"
                          : "#959595",
                        border:
                          selectedValue === value
                            ? "1.5px solid black"
                            : "1.5px solid transparent",
                        cursor: isAvailable ? "pointer" : "not-allowed",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 0,
                        "& > *": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                      onClick={() => isAvailable && handleOptionChange(optionType?.name, value)}
                    >
                      <Box
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                        title={value}
                      >
                        {value}
                      </Box>
                      {!isAvailable && <Box className="line" />}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        }
      })}
    </>
  );
});

DynamicVariantSection.displayName = "DynamicVariantSection";

export default DynamicVariantSection;

